import CommonConstants from "@/utils/constants/CommonConstants.ts";
import ObjectChecker from "@/utils/common/ObjectChecker.ts";


export class StringUtils {
  static replaceArNumerals(text: string): string {
    const arNumbersArray: string[] = ['٠', '١', '٢', '٣', '٤', '٥', '٦', '٧', '٨', '٩'];
    const enNumbersArray: string[] = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];
    if (!text)
      return text;
    for (let i = 0; i < arNumbersArray.length; i++)
      text = text.toString().replaceAll(arNumbersArray[i], enNumbersArray[i]);
    return text;
  }

  static toCSVLineWithSep(separator: string, objects: any[] | any) {
    if (!objects)
      return "";
    let line = "";
    for (let i = 0; i < objects.length; i++) {
      line += objects[i] === null ? "" : objects[i];
      if (i < objects.length - 1) {
        line += separator;
      }
    }
    return line;
  }

  static substringBeforeLast(str: string, separator: string) {
    if (!str || !separator)
      return str;
    if (separator.length == 0)
      return "";
    let pos = str.lastIndexOf(separator);
    if (pos === -1)
      return str;
    return str.substring(0, pos);
  }

  static normalizeArabic(arabic: string) {
    return this.replaceChars(arabic, CommonConstants.ARABIC_REPLACE___, CommonConstants.ARABIC_REPLACE_BY);
  }

  static arabicToEnglish(str: string) {
    return this.replaceChars(str, CommonConstants.ARABIC_KEYBOARD_, CommonConstants.ENGLISH_KEYBOARD);
  }

  static englishToArabic(str: string) {
    return this.replaceChars(str, CommonConstants.ENGLISH_KEYBOARD, CommonConstants.ARABIC_KEYBOARD_);
  }

  static replaceChars(str: string, replace: string[], replaceBy: string[]) {
    if (ObjectChecker.isEmptyOrNull(str))
      return str;
    for (let i = 0; i < replace.length; i++)
      str = str.replaceAll(replace[i], replaceBy[i]);
    return str;
  }

  static equalsIgnoreCase(o1?: string, o2?: string) {
    if ((!o1 && o1 !== '') || (!o2 && o2 !== ''))
      return false;
    return o1.toLowerCase() === o2.toLowerCase()
  }

  static csvLineToList(csv: string | null | undefined, separator?: string | RegExp, trim?: boolean, addEmpty?: boolean) {
    if (!csv)
      csv = "";
    let parts: string[] = csv.split(separator ?? ",");
    let list: string[] = [];
    for (let part of parts) {
      if (trim)
        part = part.trim();
      if (addEmpty || part)
        list.push(part);
    }
    return list;
  }

  static substringBefore(str?: string | null, separator?: string | null) {
    if (!str || !separator)
      return str;
    if (separator.length === 0)
      return "";
    let pos = str.indexOf(separator);
    if (pos === -1)
      return str;
    return str.substring(0, pos);
  }

  static getAllInnerStrings(source: string, openBracket: string, closedBracket: string) {
    let innerStrings: string[] = [];
    let current;
    while ((current = this.getInnerString(source, openBracket, closedBracket)) != null) {
      innerStrings.push(current);
      source = source.replace(openBracket + current + closedBracket, "&_$rp(^_^)cd$_&");
    }
    return innerStrings;
  }

  static getInnerString(source: string, openBracket: string, closedBracket: string) {
    return this.getInnerString2(source, openBracket, closedBracket, "####null#####");
  }

  static getInnerString2(source: string, openBracket: string, closedBracket: string, innerValue: string) {
    if (!source)
      return null;
    let startIndex = source.indexOf(openBracket);
    if (startIndex < 0)
      return null;
    let inners = 0;
    let endIndex = -1;
    for (let i = startIndex + openBracket.length; i < source.length; i++) {
      if (source.substring(i).startsWith(innerValue)) {
        inners++;
      } else if (source.substring(i).startsWith(closedBracket)) {
        if (inners == 0) {
          endIndex = i;
          break;
        } else {
          inners--;
        }
      }
    }
    if (endIndex < 0)
      return null;
    return source.substring(startIndex + openBracket.length, endIndex);
  }

  static toKebabCase(text?: string) {
    return text?.replace(/([A-Z])/g, (match, p1, index) => (index > 0 ? "-" + p1.toLowerCase() : p1.toLowerCase()))
  }

  static toPascalCase(text?: string) {
    return text?.replace(/(^\w|-\w)/g, (match) => match.replace("-", "").toUpperCase()); //my-icon-name => MyIconName
  }

  static camelCaseToSeparatedWords(input?: string | null): string {
    if (!input)
      return "";
    const separated = input.replace(/([A-Z])/g, ' $1').trim();
    return separated
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  static substringAfterLast(str: string | undefined, separator: string | undefined): string {
    if (!str || !separator)
      return '';
    if (separator.length == 0)
      return '';
    let pos: number = str.lastIndexOf(separator);
    if (pos == -1)
      return '';
    return str.substring(pos + separator.length);
  }

  // The text a user actually sees for an HTML fragment: hidden elements dropped, entities decoded,
  // <br> kept as a line break. DOMParser documents are inert, so scripts and images never run or load.
  static htmlToVisibleText(html: unknown): string {
    if (html == null)
      return '';
    const text = String(html);
    if (!/[<&]/.test(text))
      return text;
    const doc = new DOMParser().parseFromString(text, 'text/html');
    doc.body.querySelectorAll('script, style, template, [hidden]').forEach(el => el.remove());
    doc.body.querySelectorAll<HTMLElement>('[style]').forEach(el => {
      if (el.style.display === 'none' || el.style.visibility === 'hidden')
        el.remove();
    });
    doc.body.querySelectorAll('br').forEach(br => br.replaceWith('\n'));
    return (doc.body.textContent ?? '').replace(/[^\S\n]+/g, ' ').replace(/ *\n */g, '\n').trim();
  }

  static reverseTextIfArabic(text: string): string {
    if (/[\u0600-\u06FF\u0750-\u077F]/.test(text))
      return text.split(' ').reverse().join(' ');
    return text;
  }
}
